import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Paper, Typography, Grid, TextField, MenuItem, Button,
  RadioGroup, FormControlLabel, Radio, Dialog, DialogTitle,
  DialogContent, DialogActions, Autocomplete, Chip, Alert,
  Snackbar, CircularProgress, Divider,
} from '@mui/material';
import MeetingEditor from './MeetingEditor';
import api from '../../api';

const INITIAL_FORM = {
  meeting_category: 'UPCOMING',
  subject: '',
  purpose_id: '',
  location_id: '',
  department_id: '',
  meeting_date: '',
  start_time: '',
  end_time: '',
  attendees: [],
  meeting_notes: '',
  post_meeting_notes: '',
};

const SUBJECT_MAX_LENGTH = 30;

// ---------- Shared field styles (ExitModal-style) ----------
const fieldSx = {
  '& .MuiInputBase-root': { height: 40, fontSize: '14px' },
  '& .MuiInputBase-input': { fontSize: '14px' },
  '& .MuiSelect-select': { display: 'flex', alignItems: 'center', fontSize: '14px' },
};

const autocompleteSx = {
  '& .MuiInputBase-root': {
    minHeight: 40,
    fontSize: '14px',
    paddingTop: '4px',
    paddingBottom: '4px',
    flexWrap: 'wrap',
  },
  '& .MuiInputBase-input': { fontSize: '14px' },
  '& .MuiChip-root': { height: 24, fontSize: '12px' },
};

const selectProps = {
  displayEmpty: true,
  MenuProps: {
    anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
    transformOrigin: { vertical: 'top', horizontal: 'left' },
    sx: { zIndex: 9999 },
  },
};

const PreviewRow = ({ label, value }) => (
  <Grid size={{ xs: 12, sm: 6 }}>
    <Typography variant="subtitle2" color="text.secondary">{label}</Typography>
    <Typography variant="body1">{value || '—'}</Typography>
  </Grid>
);

const stripHtml = (html) => (html || '').replace(/<[^>]*>/g, '').trim();

const CreateMeeting = () => {
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [masters, setMasters] = useState({
    purposes: [], locations: [], depts: [], employees: [],
  });
  const [loadingMasters, setLoadingMasters] = useState(true);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState({ open: false, msg: '', type: 'success' });

  const showToast = useCallback((msg, type = 'success') => {
    setToast({ open: true, msg, type });
  }, []);
  const closeToast = useCallback(() => setToast((p) => ({ ...p, open: false })), []);
  const resetForm = useCallback(() => setFormData(INITIAL_FORM), []);

  useEffect(() => {
    let isMounted = true;
    const fetchMasters = async () => {
      try {
        const [purpRes, locRes, deptRes, empRes] = await Promise.all([
          api.get('/meeting-views/purposes'),
          api.get('/meeting-views/locations'),
          api.get('/meeting-views/departments'),
          api.get('/employees'),
        ]);
        if (!isMounted) return;
        setMasters({
          purposes: purpRes?.data || [],
          locations: locRes?.data || [],
          depts: deptRes?.data || [],
          employees: empRes?.data?.data || empRes?.data || [],
        });
      } catch (err) {
        if (isMounted) showToast('Failed to load master data.', 'error');
      } finally {
        if (isMounted) setLoadingMasters(false);
      }
    };
    fetchMasters();
    return () => { isMounted = false; };
  }, [showToast]);

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleAttendeesChange = useCallback((_, newValue) => {
    setFormData((prev) => ({ ...prev, attendees: newValue }));
  }, []);

  const validateForm = useCallback(() => {
    const f = formData;
    if (!f.subject.trim()) return 'Subject is required.';
    if (!f.purpose_id) return 'Meeting purpose is required.';
    if (!f.location_id) return 'Location is required.';
    if (!f.meeting_date) return 'Meeting date is required.';
    if (!f.start_time || !f.end_time) return 'Start and end times are required.';
    if (f.start_time >= f.end_time) return 'End time must be after start time.';
    if (f.attendees.length === 0) return 'Please select at least one attendee.';
    if (!stripHtml(f.meeting_notes)) return 'Meeting notes / agenda is required.';
    if (f.meeting_category === 'COMPLETED' && !stripHtml(f.post_meeting_notes)) {
      return 'Meeting outcome / MoM is required for completed meetings.';
    }
    return null;
  }, [formData]);

  const handlePreview = useCallback((e) => {
    e.preventDefault();
    const err = validateForm();
    if (err) return showToast(err, 'error');
    setPreviewOpen(true);
  }, [validateForm, showToast]);

  const handleConfirm = useCallback(async () => {
    setSubmitting(true);
    try {
      const payload = {
        ...formData,
        subject: formData.subject.trim(),
        attendees: formData.attendees.map((a) => a.emp_id),
        department_id: formData.department_id || null,
      };
      const res = await api.post('/meetings/create', payload);
      showToast(res?.data?.message || 'Meeting created successfully!', 'success');
      setPreviewOpen(false);
      resetForm();
    } catch (err) {
      showToast(err?.response?.data?.detail || 'Error creating meeting.', 'error');
    } finally {
      setSubmitting(false);
    }
  }, [formData, showToast, resetForm]);

  const isCompleted = formData.meeting_category === 'COMPLETED';

  const selectedPurpose = useMemo(
    () => masters.purposes.find((p) => p.id === formData.purpose_id)?.purpose_name,
    [masters.purposes, formData.purpose_id]
  );
  const selectedLocation = useMemo(
    () => masters.locations.find((l) => l.id === formData.location_id)?.location_name,
    [masters.locations, formData.location_id]
  );
  const selectedDept = useMemo(
    () => masters.depts.find((d) => d.id === formData.department_id)?.name,
    [masters.depts, formData.department_id]
  );

  // ============ MUI v6/v7 Grid size prop ============
  const colSize = { xs: 12, sm: 6, md: 4 };

  return (
    <Paper elevation={3} sx={{ p: { xs: 2, sm: 3, md: 4 }, borderRadius: 2 }}>
      <Typography variant="h6" color="primary" sx={{ fontWeight: 'bold' }}>
        Register Meeting
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Fill in the details below to schedule a new meeting.
      </Typography>
      <Divider sx={{ mb: 3 }} />

      <Box component="form" onSubmit={handlePreview} noValidate>
        {/* Category */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
            Meeting Category
          </Typography>
          <RadioGroup
            row
            name="meeting_category"
            value={formData.meeting_category}
            onChange={handleChange}
          >
            <FormControlLabel value="UPCOMING" control={<Radio />} label="Upcoming Meeting" />
            <FormControlLabel value="COMPLETED" control={<Radio />} label="Completed Meeting" />
          </RadioGroup>
        </Box>

        {/* Row 1 */}
        <Grid container spacing={3}>
          <Grid size={colSize}>
            <TextField
              fullWidth
              required
              label="Subject"
              name="subject"
              inputProps={{ maxLength: SUBJECT_MAX_LENGTH }}
              value={formData.subject}
              onChange={handleChange}
              helperText={`${formData.subject.length}/${SUBJECT_MAX_LENGTH} characters`}
              sx={fieldSx}
            />
          </Grid>

          <Grid size={colSize}>
            <TextField
              select
              fullWidth
              required
              label="Meeting Purpose"
              name="purpose_id"
              value={formData.purpose_id}
              onChange={handleChange}
              disabled={loadingMasters}
              SelectProps={selectProps}
              sx={fieldSx}
            >
              {masters.purposes.length === 0 && (
                <MenuItem value="" disabled><em>Loading…</em></MenuItem>
              )}
              {masters.purposes.map((p) => (
                <MenuItem key={p.id} value={p.id}>{p.purpose_name}</MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid size={colSize}>
            <TextField
              select
              fullWidth
              required
              label="Location"
              name="location_id"
              value={formData.location_id}
              onChange={handleChange}
              disabled={loadingMasters}
              SelectProps={selectProps}
              sx={fieldSx}
            >
              {masters.locations.length === 0 && (
                <MenuItem value="" disabled><em>Loading…</em></MenuItem>
              )}
              {masters.locations.map((l) => (
                <MenuItem key={l.id} value={l.id}>{l.location_name}</MenuItem>
              ))}
            </TextField>
          </Grid>

          {/* Row 2 */}
          <Grid size={colSize}>
            <TextField
              fullWidth
              required
              type="date"
              label="Meeting Date"
              name="meeting_date"
              InputLabelProps={{ shrink: true }}
              value={formData.meeting_date}
              onChange={handleChange}
              sx={fieldSx}
            />
          </Grid>

          <Grid size={colSize}>
            <TextField
              fullWidth
              required
              type="time"
              label="Start Time"
              name="start_time"
              InputLabelProps={{ shrink: true }}
              value={formData.start_time}
              onChange={handleChange}
              sx={fieldSx}
            />
          </Grid>

          <Grid size={colSize}>
            <TextField
              fullWidth
              required
              type="time"
              label="End Time"
              name="end_time"
              InputLabelProps={{ shrink: true }}
              value={formData.end_time}
              onChange={handleChange}
              sx={fieldSx}
            />
          </Grid>

          {/* Row 3 — Department (1 col) + Attendees (2 cols) */}
          <Grid size={colSize}>
            <TextField
              select
              fullWidth
              label="Department (Optional)"
              name="department_id"
              value={formData.department_id}
              onChange={handleChange}
              disabled={loadingMasters}
              SelectProps={selectProps}
              sx={fieldSx}
            >
              <MenuItem value=""><em>None</em></MenuItem>
              {masters.depts.map((d) => (
                <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid size={{ xs: 12, sm: 12, md: 8 }}>
            <Autocomplete
              multiple
              options={masters.employees}
              loading={loadingMasters}
              getOptionLabel={(o) =>
                `${o.emp_id} - ${o.first_name} ${o.last_name || ''}`.trim()
              }
              isOptionEqualToValue={(o, v) => o.emp_id === v.emp_id}
              value={formData.attendees}
              onChange={handleAttendeesChange}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    variant="outlined"
                    size="small"
                    label={`${option.first_name} ${option.last_name || ''}`.trim()}
                    {...getTagProps({ index })}
                    key={option.emp_id}
                  />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Attendees"
                  required={formData.attendees.length === 0}
                  placeholder="Search employees..."
                  sx={autocompleteSx}
                />
              )}
              sx={{ width: '100%' }}
            />
          </Grid>
        </Grid>

        {/* Rich-text */}
        <Box sx={{ mt: 3 }}>
          <MeetingEditor
            label="Meeting Notes / Agenda"
            value={formData.meeting_notes}
            onChange={(content) => setFormData((p) => ({ ...p, meeting_notes: content }))}
            required
          />
        </Box>

        {isCompleted && (
          <Box sx={{ mt: 2 }}>
            <MeetingEditor
              label="Meeting Outcome / MoM"
              value={formData.post_meeting_notes}
              onChange={(content) =>
                setFormData((p) => ({ ...p, post_meeting_notes: content }))
              }
              required
            />
          </Box>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
          <Button
            type="submit"
            variant="contained"
            size="large"
            disabled={loadingMasters || submitting}
            sx={{ px: 4 }}
          >
            Preview & Save
          </Button>
        </Box>
      </Box>

      {/* Preview Modal */}
      <Dialog
        open={previewOpen}
        onClose={() => !submitting && setPreviewOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: 'primary.main', color: '#fff' }}>
          Confirm Meeting Details
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <PreviewRow label="Subject" value={formData.subject} />
            <PreviewRow label="Category" value={formData.meeting_category} />
            <PreviewRow label="Purpose" value={selectedPurpose} />
            <PreviewRow label="Location" value={selectedLocation} />
            <PreviewRow label="Department" value={selectedDept || 'None'} />
            <PreviewRow label="Date" value={formData.meeting_date} />
            <PreviewRow
              label="Time"
              value={
                formData.start_time && formData.end_time
                  ? `${formData.start_time} - ${formData.end_time}`
                  : ''
              }
            />

            <Grid size={{ xs: 12 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Attendees ({formData.attendees.length})
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                {formData.attendees.map((a) => (
                  <Chip key={a.emp_id} label={`${a.emp_id} - ${a.first_name}`} size="small" />
                ))}
              </Box>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
                Agenda / Notes
              </Typography>
              <Box
                sx={{ '& p': { my: 0.5 }, '& ul, & ol': { pl: 3, my: 0.5 } }}
                dangerouslySetInnerHTML={{ __html: formData.meeting_notes || '—' }}
              />
            </Grid>

            {isCompleted && (
              <Grid size={{ xs: 12 }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
                  Outcome / MoM
                </Typography>
                <Box
                  sx={{ '& p': { my: 0.5 }, '& ul, & ol': { pl: 3, my: 0.5 } }}
                  dangerouslySetInnerHTML={{ __html: formData.post_meeting_notes || '—' }}
                />
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setPreviewOpen(false)} variant="outlined" disabled={submitting}>
            Go Back
          </Button>
          <Button
            onClick={handleConfirm}
            variant="contained"
            color="success"
            disabled={submitting}
            startIcon={submitting ? <CircularProgress size={20} color="inherit" /> : null}
          >
            {submitting ? 'Creating...' : 'Confirm & Create'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={closeToast}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={toast.type} variant="filled" onClose={closeToast}>
          {toast.msg}
        </Alert>
      </Snackbar>
    </Paper>
  );
};

export default CreateMeeting;